/**
 * React Query wrappers for the email-notices API (#1911).
 *
 * Query keys
 *   - `['email-notices']`                — root; every mutation invalidates it
 *   - `['email-notices', 'list', status]` — one status tab
 *   - `['email-notices', 'detail', id]`   — notice + recipients + proofs
 *
 * The recipient PREVIEW is a mutation, not a query, even though it reads.
 * Making it a query would let React Query refetch it on focus or reconnect,
 * which would silently replace the candidate list an operator has been
 * striking rows out of — against a newer instant, with their exclusions
 * pointing at rows that may no longer be there. Recomputing is an explicit
 * operator act, so it is modelled as one.
 *
 * Nothing here invalidates the model catalog any more. #1877's `/execute`
 * flipped a shelf from this page, so the models table went stale on a send;
 * a notice now changes no catalog row, and an invalidation that fired anyway
 * would be a claim this surface can no longer make.
 */
import type {
  CreateEmailNoticeRequest,
  EmailNotice,
  EmailNoticeDetail,
  EmailNoticeRecipientPreviewResponse,
  EmailNoticeSendResponse,
  EmailNoticeStatus,
  EmailNoticeTestSendRequest,
  EmailNoticeTestSendResponse,
  FinalizeEmailNoticeRecipientsResponse,
  NoticeAudience,
  UpdateEmailNoticeRequest,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  cancelEmailNotice,
  createEmailNotice,
  deleteEmailNotice,
  finalizeEmailNoticeRecipients,
  getEmailNotice,
  listEmailNotices,
  previewEmailNoticeAudience,
  sendEmailNotice,
  testSendEmailNotice,
  updateEmailNotice,
} from './emailNotices.ts';

export const EMAIL_NOTICES_KEY = ['email-notices'] as const;

export function emailNoticeListKey(
  status: EmailNoticeStatus | null,
): readonly ['email-notices', 'list', EmailNoticeStatus | null] {
  return [...EMAIL_NOTICES_KEY, 'list', status] as const;
}

export function emailNoticeDetailKey(
  id: string,
): readonly ['email-notices', 'detail', string] {
  return [...EMAIL_NOTICES_KEY, 'detail', id] as const;
}

function invalidateAfterMutation(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: EMAIL_NOTICES_KEY });
}

export function useEmailNotices(
  status: EmailNoticeStatus | null,
): UseQueryResult<readonly EmailNotice[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: emailNoticeListKey(status),
    queryFn: ({ signal }) => listEmailNotices(status, fetchImpl, signal),
  });
}

export function useEmailNoticeDetail(id: string | null): UseQueryResult<EmailNoticeDetail> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: emailNoticeDetailKey(id ?? ''),
    queryFn: ({ signal }) => getEmailNotice(id ?? '', fetchImpl, signal),
    enabled: id !== null && id.length > 0,
  });
}

/**
 * Seed the detail cache from a mutation's own response instead of waiting for
 * the invalidated read. Every one of these endpoints returns the whole detail
 * or notice it just wrote, and the send panel's gate lamps are computed from
 * it — a refetch round trip would show the pre-write gates as current for as
 * long as it takes, which is exactly the window an operator would press again
 * in.
 */
function seedDetail(qc: QueryClient, detail: EmailNoticeDetail): void {
  qc.setQueryData(emailNoticeDetailKey(detail.notice.id), detail);
}

export function useCreateEmailNotice(): UseMutationResult<
  EmailNoticeDetail,
  Error,
  CreateEmailNoticeRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEmailNoticeRequest) => createEmailNotice(body, fetchImpl),
    onSuccess: (detail) => {
      seedDetail(qc, detail);
      invalidateAfterMutation(qc);
    },
  });
}

export function useUpdateEmailNotice(): UseMutationResult<
  EmailNoticeDetail,
  Error,
  { readonly id: string; readonly body: UpdateEmailNoticeRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => updateEmailNotice(id, body, fetchImpl),
    onSuccess: (detail) => {
      seedDetail(qc, detail);
      invalidateAfterMutation(qc);
    },
  });
}

export function useDeleteEmailNotice(): UseMutationResult<
  { readonly deleted: boolean; readonly id: string },
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteEmailNotice(id, fetchImpl),
    onSuccess: (_result, id) => {
      qc.removeQueries({ queryKey: emailNoticeDetailKey(id) });
      invalidateAfterMutation(qc);
    },
  });
}

/**
 * Keyed on the audience, not on a notice (#1920), so the same mutation answers
 * "who does this reach" for a draft that has never been saved and for one that
 * has. Two code paths would mean the unsaved one is the untested one.
 */
export function usePreviewEmailNoticeAudience(): UseMutationResult<
  EmailNoticeRecipientPreviewResponse,
  Error,
  NoticeAudience
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: (audience: NoticeAudience) => previewEmailNoticeAudience(audience, fetchImpl),
  });
}

export function useFinalizeEmailNoticeRecipients(): UseMutationResult<
  FinalizeEmailNoticeRecipientsResponse,
  Error,
  { readonly id: string; readonly excludeAccountIds: readonly string[] }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, excludeAccountIds }) =>
      finalizeEmailNoticeRecipients(id, { exclude_account_ids: excludeAccountIds }, fetchImpl),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: emailNoticeDetailKey(result.notice.id) });
      invalidateAfterMutation(qc);
    },
  });
}

export function useTestSendEmailNotice(): UseMutationResult<
  EmailNoticeTestSendResponse,
  Error,
  { readonly id: string; readonly body: EmailNoticeTestSendRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => testSendEmailNotice(id, body, fetchImpl),
    onSuccess: (result) => {
      // Fold the freshly written proof into the cached detail rather than
      // waiting for the invalidated read. Gate two is computed from
      // `proven_brands`, so the refetch window is a window in which a
      // just-rehearsed brand still reads as unrehearsed — harmless in
      // direction, but it makes a successful test send look like it did
      // nothing, which invites a second one. Only server-returned values are
      // written; `recipients` is left exactly as it was because this response
      // says nothing about it.
      qc.setQueryData<EmailNoticeDetail>(emailNoticeDetailKey(result.notice.id), (previous) =>
        previous === undefined
          ? previous
          : { ...previous, notice: result.notice, proven_brands: result.proven_brands },
      );
      void qc.invalidateQueries({ queryKey: emailNoticeDetailKey(result.notice.id) });
      invalidateAfterMutation(qc);
    },
  });
}

export function useSendEmailNotice(): UseMutationResult<
  EmailNoticeSendResponse,
  Error,
  { readonly id: string; readonly accountIds?: readonly string[] }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, accountIds }) =>
      sendEmailNotice(id, accountIds === undefined ? {} : { account_ids: accountIds }, fetchImpl),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: emailNoticeDetailKey(result.notice.id) });
      invalidateAfterMutation(qc);
    },
  });
}

export function useCancelEmailNotice(): UseMutationResult<
  { readonly notice: EmailNotice },
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelEmailNotice(id, fetchImpl),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: emailNoticeDetailKey(result.notice.id) });
      invalidateAfterMutation(qc);
    },
  });
}
